import React from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FieldErrors as ReactHookFormFieldErrors,
  useFormContext,
} from "react-hook-form";
import { v4 as uuidV4 } from "uuid";

import { FieldErrors } from "src/api/types";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { DropDown, RenderValueFn } from "src/base-components/DropDown";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import { Tooltip } from "src/design-system/Tooltip";
import {
  ExpressionWebhookTimeoutConfig,
  InboundWebhookConnectionForm,
  LegacyWebhookTimeoutConfig,
} from "src/inboundWebhookNode/types";

// Matches strings that are negative integers,
// but also strings that contain just a dash ("-").
const NEGATIVE_INTEGER_OR_DASH_REGEX = /^-\d*$/;

// Matches strings that are decimals (negative or positive),
// but also strings that contain just a period (".").
const DECIMAL_OR_PERIOD_REGEX = /^-?\d*\.\d*$/;

const TIMEOUT_LIMITS = {
  minutes: 84960,
  hours: 1416,
  days: 59,
} as const;

const timeoutActionOptions = [
  { key: "continue", value: "Continue decision despite timeout" },
  { key: "fail", value: "Stop and fail decision" },
];

type TimeoutUnit = keyof typeof TIMEOUT_LIMITS;

const getMaxTimeout = (unit: TimeoutUnit): number => TIMEOUT_LIMITS[unit];

const isExpressionWebhookTimeoutConfig = (
  webhookTimeoutConfig:
    | ExpressionWebhookTimeoutConfig
    | LegacyWebhookTimeoutConfig,
) => typeof webhookTimeoutConfig.webhook_timeout !== "string";

const renderValue: RenderValueFn<string> = ({ value }) => (
  <div className="w-56 whitespace-nowrap px-4 py-2">{value}</div>
);

const renderButtonValue = (value?: string) => (
  <div className="flex flex-grow items-center justify-between py-2.5 pr-3 pl-4">
    <div className="mr-0.5 grow text-left text-gray-800">{value}</div>
    <div className="flex h-auto items-center">
      <Icon color="text-gray-500" icon={faChevronDown} size="xs" />
    </div>
  </div>
);

type Props = {
  immutable: boolean;
  runFieldErrors: FieldErrors | undefined;
};

export const WebhookTimeoutInput: React.FC<Props> = ({
  immutable,
  runFieldErrors,
}) => {
  const { register, trigger, control, formState, getValues, setValue, watch } =
    useFormContext<InboundWebhookConnectionForm>();

  const webhookTimeoutConfig = watch("config.webhook_timeout_config");

  const webhookTimeoutFormStateError =
    // `react-hook-form` `FieldErrors` do not play well with nested disjoint union type fields
    // https://github.com/react-hook-form/react-hook-form/issues/8584
    (
      formState.errors.config?.webhook_timeout_config
        ?.webhook_timeout as ReactHookFormFieldErrors<{ expression: string }>
    )?.expression?.message || undefined;
  const webhookTimeoutRunFieldError =
    runFieldErrors?.[
      getValues("config.webhook_timeout_config.webhook_timeout.id")
    ];
  const webhookTimeoutError =
    webhookTimeoutRunFieldError || webhookTimeoutFormStateError;

  const legacyWebhookTimeoutInput = register(
    "config.webhook_timeout_config.webhook_timeout",
  );

  const upgradeToExpressionTimeoutConfigIfLegacy = () => {
    const webhookTimeoutConfig = getValues("config.webhook_timeout_config");

    if (!isExpressionWebhookTimeoutConfig(webhookTimeoutConfig!)) {
      // `timeout_seconds` is deprecated
      const {
        timeout_seconds: _,
        ...webhookTimeoutConfigWithoutTimeoutSeconds
      } = webhookTimeoutConfig as LegacyWebhookTimeoutConfig;
      setValue("config.webhook_timeout_config", {
        ...webhookTimeoutConfigWithoutTimeoutSeconds,
        webhook_timeout: {
          id: uuidV4(),
          expression: webhookTimeoutConfig!.webhook_timeout as string,
        },
      });
    }
  };

  return (
    <>
      <div className="mb-2.5 flex items-start justify-between">
        <div className="flex flex-col">
          <h3 className="font-inter-medium-12px text-gray-700">
            Set a timeout for webhook
          </h3>
          <div className="block w-full whitespace-normal font-inter-normal-12px text-gray-500">
            Control how long to wait for a webhook response and what action to
            take if the timeout occurs
          </div>
        </div>
        <Controller
          name="config.webhook_timeout_config.active"
          render={({ field }) => (
            <Switch
              dataLoc="webhook-connection-node-timeout-config-active"
              disabled={immutable}
              enabled={field.value}
              onChange={() => {
                // Pre-timeout Nodes do not have `webhook_timeout_config` at all.
                // We add it at first enable time.
                if (webhookTimeoutConfig === undefined) {
                  setValue("config.webhook_timeout_config", {
                    active: true,
                    webhook_timeout: {
                      id: uuidV4(),
                      expression: "10",
                    },
                    timeout_action: "fail",
                    webhook_timeout_unit: "days",
                  });
                } else {
                  field.onChange(!field.value);
                  upgradeToExpressionTimeoutConfigIfLegacy();
                }
              }}
            />
          )}
        />
      </div>

      {webhookTimeoutConfig?.active && (
        <div className="mb-2 w-full items-center rounded-lg bg-gray-50 pt-3 pr-4 pb-4 pl-4">
          <div className="flex flex-wrap gap-2">
            <div className="flex min-w-[200px] flex-1 flex-col gap-1">
              <div className="flex items-center font-inter-normal-12px text-gray-700">
                Wait duration
              </div>
              {isExpressionWebhookTimeoutConfig(webhookTimeoutConfig) ? (
                <Controller
                  control={control}
                  name="config.webhook_timeout_config.webhook_timeout.expression"
                  render={({ field }) => (
                    <AutocompleteCodeInput
                      dataLoc="in-webhook-node-timeout-expression"
                      disabled={immutable}
                      error={webhookTimeoutError}
                      value={field.value}
                      onChange={(newValue) => {
                        if (newValue === "") {
                          trigger(
                            "config.webhook_timeout_config.webhook_timeout.expression",
                          );
                        }
                        field.onChange(newValue);
                      }}
                    />
                  )}
                  rules={{
                    validate: (value) => {
                      if (!webhookTimeoutConfig.active) return true;

                      if (value === "") return "The value may not be empty";

                      // If value is a non-negative integer...
                      if (/^\d+$/.test(value)) {
                        const numberValue = Number(value);

                        // ...it must also be positive...
                        if (numberValue <= 0) {
                          return "The value is not a positive whole number or an expression";
                        }

                        // ...and lower than limit for given unit.
                        // (This also implicitly validates that the value is >=60 seconds
                        // since the minimum value/unit combination is 1 minute.)
                        const valueLimit = getMaxTimeout(
                          webhookTimeoutConfig.webhook_timeout_unit as keyof typeof TIMEOUT_LIMITS,
                        );
                        if (numberValue > valueLimit) {
                          return `The value exceeds the maximum allowed timeout of ${valueLimit} ${webhookTimeoutConfig.webhook_timeout_unit}`;
                        }

                        // If not...
                      } else {
                        // ...it must not be a negative integer or a float
                        // (or just a dash or period by itself).
                        if (
                          NEGATIVE_INTEGER_OR_DASH_REGEX.test(value) ||
                          DECIMAL_OR_PERIOD_REGEX.test(value)
                        ) {
                          return "The value is not a positive whole number or an expression";
                        }
                        // It can still be any other arbitrary expression (evaluated at runtime).
                      }

                      return true;
                    },
                  }}
                />
              ) : (
                <Tooltip
                  body={webhookTimeoutError}
                  disabled={!webhookTimeoutError}
                >
                  <Input
                    data-loc="webhook-connection-node-timeout-config-timeout-config"
                    disabled={immutable}
                    errored={!!webhookTimeoutError}
                    placeholder="Enter timeout"
                    fullWidth
                    {...legacyWebhookTimeoutInput}
                    onChange={(e) => {
                      legacyWebhookTimeoutInput.onChange(e);
                      upgradeToExpressionTimeoutConfigIfLegacy();
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <div className="flex min-w-[200px] max-w-[300px] flex-1 flex-col gap-1">
              <div className="flex items-center font-inter-normal-12px text-gray-700">
                Time unit
              </div>
              <Controller
                control={control}
                name="config.webhook_timeout_config.webhook_timeout_unit"
                render={({ field }) => (
                  <DropDown
                    buttonClassName="h-8 py-2 text-base box-border border border-gray-200 flex items-center"
                    className="w-full"
                    disabled={immutable}
                    elements={[
                      { key: "minutes", value: "Minutes" },
                      { key: "hours", value: "Hours" },
                      { key: "days", value: "Days" },
                    ]}
                    itemsClassNames="overflow-y-auto max-h-80 decideScrollbar"
                    renderButtonValue={renderButtonValue}
                    renderValue={renderValue}
                    selectedKey={field.value}
                    onSelect={(key) => {
                      field.onChange(key);
                      upgradeToExpressionTimeoutConfigIfLegacy();
                    }}
                  />
                )}
              />
            </div>
            <div className="mt-2 flex w-full flex-col gap-1">
              <div className="flex items-center font-inter-normal-12px text-gray-700">
                Action on timeout
              </div>
              <Controller
                control={control}
                name="config.webhook_timeout_config.timeout_action"
                render={({ field }) => (
                  <DropDown
                    buttonClassName="h-8 py-2 text-base box-border border border-gray-200 flex items-center"
                    className="w-full"
                    disabled={immutable}
                    elements={timeoutActionOptions}
                    itemsClassNames="overflow-y-auto max-h-80 decideScrollbar"
                    renderButtonValue={renderButtonValue}
                    renderValue={renderValue}
                    selectedKey={field.value}
                    onSelect={(key) => {
                      field.onChange(key);
                      upgradeToExpressionTimeoutConfigIfLegacy();
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
